import { CookieManager } from '../utils/cookieManager';
import { getAttribution, getAttributionParams } from '../utils/attribution';

export const initializeAnalytics = (measurementId: string) => {
   // GA activé par défaut - conforme RGPD français avec IP anonymisé
   // Les cookies de mesure d'audience sont exemptés du consentement préalable
   const consent = CookieManager.getConsent();

   // Utiliser un chargement différé pour GA
   const loadGA = () => {
     // Vérifier si GA est déjà chargé
     if ((window as any).gaLoaded) return;
     (window as any).gaLoaded = true;

     const script = document.createElement('script');
     script.async = true;
     script.defer = true; // Ajouter l'attribut defer
     script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

     script.onload = () => {
         (window as any).dataLayer = (window as any).dataLayer || [];
         function gtag(...args: any[]) {
             // Supprimer ce log en production pour optimiser davantage
             if (import.meta.env.DEV) {
                 console.log('GA Event:', args);
             }
             (window as any).dataLayer.push(arguments);
         }
         (window as any).gtag = gtag;

         // Configuration de base
         gtag('js', new Date());
         gtag('config', measurementId, {
             'cookie_flags': 'SameSite=None;Secure',
             'anonymize_ip': true, // Pour RGPD
             'cookie_domain': window.location.hostname,
             'cookie_expires': 365 * 24 * 60 * 60, // 1 an en secondes
             'send_page_view': false // Différer l'envoi des vues de page
         });

         // Configuration du consentement
         gtag('consent', 'default', {
             'analytics_storage': 'granted',
             'functionality_storage': consent?.preferences ? 'granted' : 'denied',
             'security_storage': 'granted'
         });

         // Attribution : first-touch en user properties, last-touch sur le page_view
         const attribution = getAttribution();
         if (attribution) {
             gtag('set', 'user_properties', {
                 first_ref: attribution.first.ref || undefined,
                 first_utm_source: attribution.first.utm_source || undefined,
                 first_utm_medium: attribution.first.utm_medium || undefined,
                 first_utm_campaign: attribution.first.utm_campaign || undefined,
             });
         }

         // Envoyer la page vue après initialisation (avec attribution last-touch)
         gtag('event', 'page_view', {
             page_title: document.title,
             page_location: window.location.href,
             page_path: window.location.pathname,
             ...getAttributionParams('last')
         });
     };

     script.onerror = (error) => {
         console.error('Error loading GA:', error);
     };

     document.head.appendChild(script);
   };

   // Chargement uniquement lors d'une interaction ou après un délai
   if (document.readyState === 'complete') {
     // Si la page est déjà chargée, ajouter les écouteurs d'événements
     document.addEventListener('scroll', loadGA, { once: true, passive: true });
     document.addEventListener('mousemove', loadGA, { once: true, passive: true });
     document.addEventListener('touchstart', loadGA, { once: true, passive: true });
     document.addEventListener('click', loadGA, { once: true, passive: true });
   } else {
     // Sinon, attendre que la page soit chargée
     window.addEventListener('load', () => {
       document.addEventListener('scroll', loadGA, { once: true, passive: true });
       document.addEventListener('mousemove', loadGA, { once: true, passive: true });
       document.addEventListener('touchstart', loadGA, { once: true, passive: true });
       document.addEventListener('click', loadGA, { once: true, passive: true });
     });
   }

   // Charger GA après un délai de 5 secondes même sans interaction
   setTimeout(loadGA, 5000);
};

/**
 * Fonction unique et canonique de tracking d'événements GA4.
 * - Soumise au consentement : un refus explicite (`analytics === false`) bloque l'envoi.
 * - `consent === null` (visiteur n'ayant pas encore choisi) => autorisé, cohérent avec
 *   la posture "mesure d'audience exemptée" (analytics_storage 'granted' par défaut).
 * - `extraParams` permet de joindre des paramètres GA4 personnalisés (ex. partner).
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
  extraParams?: Record<string, unknown>
) => {
   const consent = CookieManager.getConsent();
   if (consent && !consent.analytics) {
       if (import.meta.env.DEV) {
           console.log('Event tracking disabled - no consent');
       }
       return;
   }

   if ((window as any).gtag) {
       const eventParams: Record<string, unknown> = {
           event_category: category,
           event_label: label,
           ...(extraParams || {})
       };

       if (typeof value !== 'undefined') {
           eventParams.value = value;
       }

       (window as any).gtag('event', action, eventParams);
   } else if (import.meta.env.DEV) {
       console.warn('GTM not initialized');
   }
};

// Fonction pour mettre à jour le consentement
export const updateAnalyticsConsent = (hasConsent: boolean) => {
   if ((window as any).gtag) {
       (window as any).gtag('consent', 'update', {
           'analytics_storage': hasConsent ? 'granted' : 'denied'
       });
   }
};

// Fonction de debug pour le développement
export const debugAnalytics = () => {
   console.log('GA Debug Info:', {
       measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID,
       consent: CookieManager.getConsent(),
       attribution: getAttribution(),
       dataLayer: (window as any).dataLayer,
       gtag: !!(window as any).gtag
   });
};
